import { Order } from "../../../api/models";
import {
  getCore,
  getListCore,
  postCore,
  putCore,
} from "../../../api/services/axiosCore.service";

/**
 * Retrieves a specific order by ID.
 * @param {string|number} id - The ID of the order.
 * @param {Object} params - Additional parameters for the request.
 * @returns {Promise<Object>} A promise resolving to the order data.
 */
export const getOrder = (id, params) => {
  return getCore(`/orders/${id}`, params).then((res) => {
    res.results = new Order(res.results);
    return res;
  });
};

/**
 * Fetches a list of orders and maps them to `Order` instances.
 * @param {Object} params - The parameters for the request.
 * @returns {Promise<Object>} A promise resolving to a list of orders.
 */
export const gerOrderList = (params) => {
  return getListCore("/orders", params).then((res) => {
    res.results = res.results.map((item) => new Order(item));
    return res;
  });
};

/**
 * Retrieves a list of order units, transforming each result.
 * @param {Object} params - The parameters for the request.
 * @returns {Promise<Object>} A promise resolving to order units.
 */
export const getOrderUnits = async (params) => {
  const res = await getListCore(`/order_units`, params);
  return {
    ...res,
    results: res.results.map((r) => ({
      ...r,
      ...r.item,
    })),
  };
};

/**
 * Updates an existing order.
 * @param {string|number} id - The ID of the order.
 * @param {Object} params - The updated data for the order.
 * @returns {Promise<Object>} A promise resolving to the updated order.
 */
export const putOrder = async (id, params) => {
  const res = await putCore(`/orders/${id}`, params);
  res.results = new Order(res.results);
  return res;
};

/**
 * Fetches all order units across multiple pages.
 * @param {Object} params - The parameters for pagination.
 * @returns {Promise<Array>} A promise resolving to an array of order units.
 */
export const getPaginatedOrderUnits = async (params) => {
  const newParams = { ...params, page: 1 };
  let currentPage = await getOrderUnits(newParams);
  let results = currentPage.results;
  const totalPages = Math.ceil(
    Number(currentPage.headers["x-total"]) /
      Number(currentPage.headers["x-per-page"])
  );

  while (newParams.page < totalPages) {
    newParams.page++;
    currentPage = await getOrderUnits(newParams);
    results = [...results, ...currentPage.results];
  }

  return results;
};

/**
 * Sends a dispatch document (remito) for a given order.
 * @param {Object} order - The order details.
 * @param {Blob} blobFile - The PDF file to send.
 * @returns {Promise<Object>} A promise resolving to the API response.
 */
export const sendFiscalDocument = (order, blobFile) => {
  const url = `/orders/${order.id}/dispatch_document`;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blobFile);
    reader.onloadend = () => {
      let base64File = reader.result.replace(
        "data:application/pdf;base64,",
        ""
      );

      const params = { file: { data: base64File }, copies: 3 };
      resolve(postCore(url, params));
    };
  });
};

/**
 * Fetches shipping labels for a specific shipment.
 * @param {string|number} id - The order ID.
 * @param {string|number} shipmentId - The shipment ID.
 * @returns {Promise<Object>} A promise resolving to the label data.
 */
export const fetchLabels = (id, shipmentId) => {
  return getCore(`orders/${id}/shipments/${shipmentId}/labels`, {
    only: "zpl",
  });
};

/**
 * Fetches an order by its reference using the provided parameters.
 * @param {Object} params - The parameters to include in the request.
 * @returns {Promise<Object>} A promise that resolves to the order data.
 */
export const fetchOrderByReference = (params) => {
  return getCore("/orders", params);
};

/**
 * Updates multiple orders asynchronously based on the provided filters.
 * @param {Object} filters - The filters to apply when updating orders.
 * @param {Object} params - The updated data for the orders.
 * @returns {Promise<Object>} A promise that resolves to the API response.
 */
export const putOrderList = (filters, params) => {
  return putCore(`/orders/async`, params, filters);
};

/**
 * Generates order labels for a given order.
 * @param {string|number} id - The ID of the order.
 * @param {string} type - The type of label to generate.
 * @returns {Promise<Object>} A promise resolving to the label data.
 */
export const postOrderLabels = (id, body) => {
  return postCore(`/orders/${id}/labels`, body);
};

/**
 * Retrieves statistical data for orders.
 * @param {Object} filter - The filters to apply to the request.
 * @returns {Promise<Object>} A promise resolving to the statistics.
 */
export const getOrdersStats = (filter) => {
  return getCore(`/orders/stats/v2`, filter);
};
